import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {Divider, Skeleton, Stack, Typography} from '@mui/material';
import {CarePlanConfigurationFeature} from '../../../backend/enums/care-plan-configuration-feature';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../backend/requests/care-plan-activities-group';
import {scheduleReadableExpression} from '../../../utils/functions';
import {ConfigurationsIndex} from '../../../types';
import {ActivityType} from '../../../backend/interfaces/activity-type';

interface MedicalAppointmentCardContentProps {
  configurationsIndex?: ConfigurationsIndex;
  valuesIndex: {[key: string]: CarePlanActivitiesGroupApiRequestConfigurationsItem};
  activityType: ActivityType;
}

const MedicalAppointmentCardContent = ({
  configurationsIndex,
  valuesIndex,
  activityType,
}: MedicalAppointmentCardContentProps) => {
  const [medicalFormat, setMedicalFormat] = useState<string | undefined>(undefined);
  const [medicalAppointmentSpecialities, setMedicalAppointmentSpecialities] = useState<
    string | undefined
  >(undefined);

  const {t} = useTranslation();

  const consultationValuesIndexedByFeature = useMemo(() => {
    const result: {[key in CarePlanConfigurationFeature]?: string} = {};

    if (configurationsIndex && valuesIndex) {
      const featureConfigurations = Object.values(configurationsIndex).filter(
        (configuration) => !!configuration.feature
      );

      featureConfigurations.forEach((configuration) => {
        result[configuration.feature!] = valuesIndex[configuration.id!]?.carePlanConfigurationValue;

        if (configuration.feature === CarePlanConfigurationFeature.MedicalAppointmentSpecialty) {
          const valuesIds: string[] = valuesIndex[configuration.id!]!.carePlanConfigurationValue;

          const configurationNames: string[] = [];

          valuesIds.forEach((valueId) => {
            const name = configuration.arrayItems?.find((item) => item.id === valueId)?.name;
            if (name) configurationNames.push(name);
          });

          const namesExpression = configurationNames
            .join(', ')
            .replace(/,(?=[^,]*$)/, ` ${t(translations.schedule_field.readable_periodicity.and)}`);

          setMedicalAppointmentSpecialities(namesExpression);
        }

        if (configuration.feature === CarePlanConfigurationFeature.MedicalAppointmentFormat) {
          const val = configuration.arrayItems?.find(
            (item) => item.id === valuesIndex[configuration.id!]!.carePlanConfigurationValue!
          );

          setMedicalFormat(val?.name);
        }
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationsIndex, valuesIndex]);

  return (
    <Stack className="medical-appointment-card-content">
      <Typography className="name">
        {consultationValuesIndexedByFeature.name || activityType.name}
      </Typography>
      <Stack className="medical-appointment-container">
        {medicalAppointmentSpecialities ? (
          <Typography className="medical-appointment-specialities">
            {medicalAppointmentSpecialities}
          </Typography>
        ) : (
          <Skeleton
            {...{animation: 'wave', variant: 'rounded'}}
            sx={{width: '480px', height: '15px', marginRight: '56px'}}
          />
        )}
        <Stack className="medical-appointment-details">
          {medicalFormat ? (
            <Typography className="medical-appointment-format">{medicalFormat}</Typography>
          ) : (
            <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={100} height={15} />
          )}

          <Typography className="medical-appointment-notes">
            {consultationValuesIndexedByFeature.notes || ''}
          </Typography>
        </Stack>
      </Stack>
      <Divider />
      <Stack className="medical-appointment-schedule-container">
        {consultationValuesIndexedByFeature.schedule && (
          <Stack className="medical-appointment-schedule-content">
            {
              <Typography>
                {scheduleReadableExpression({
                  t,
                  scheduleDefinition: JSON.parse(consultationValuesIndexedByFeature.schedule),
                })}
              </Typography>
            }
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default MedicalAppointmentCardContent;
